import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import ReactPaginate from "react-paginate";
import { useEffect, useState } from "react";
import API from "../Network/API";
import FeaturedCourseCard, { FeaturedCourseSkeleton } from "../components/FeaturedCourseCard";
import { useTranslation } from "react-i18next";

export default function AllCourse() {
  const { t } = useTranslation();
  const categories = useSelector((state) => state.categoryReducer);
  const [currentPage, setCurrentPage] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get('keyword') || '';
  const categorySlug = searchParams.get('category') || '';
  const [query, setQuery] = useState(keyword);
  const [reloadKey, setReloadKey] = useState(0);
  // results live in local state: App.js also writes the unfiltered list into the shared
  // course slice on boot, which could land after (and overwrite) a filtered response
  const [result, setResult] = useState({ loading: true, error: false, courses: [], total_page: 0 });

  const categoryList = categories?.data || [];
  const activeCategory = categoryList.find((c) => c.slug === categorySlug);
  const activeCategoryId = activeCategory?.id;
  // a category in the URL can only be resolved to an id once the list has loaded
  const waitingForCategory = categorySlug && !activeCategory && !(categoryList.length > 0);

  // keep the input in sync when the keyword changes from elsewhere (e.g. the home hero search)
  useEffect(() => {
    setQuery(keyword);
  }, [keyword]);

  // new filters start from the first page
  useEffect(() => {
    setCurrentPage(0);
  }, [keyword, categorySlug]);

  useEffect(() => {
    if (waitingForCategory) return;
    let ignore = false;
    setResult((prev) => ({ ...prev, loading: true, error: false }));
    const params = new URLSearchParams();
    params.set('keyword', keyword);
    if (activeCategoryId) params.set('category', activeCategoryId);
    if (currentPage > 0) params.set('page', currentPage + 1);
    API.get(`/v1/oauth/all_course?${params.toString()}`)
      .then((response) => {
        if (ignore) return;
        const data = response.data.data;
        setResult({ loading: false, error: false, courses: data.courses || [], total_page: data.total_page || 0 });
      })
      .catch(() => {
        if (!ignore) setResult((prev) => ({ ...prev, loading: false, error: true }));
      });
    return () => { ignore = true; };
  }, [currentPage, keyword, activeCategoryId, waitingForCategory, reloadKey]);

  const updateParams = (changes) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(changes).forEach(([key, value]) => {
      if (value) next.set(key, value);
      else next.delete(key);
    });
    setSearchParams(next);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    updateParams({ keyword: query.trim() });
  };

  const handlePageChange = (selectedPage) => {
    setCurrentPage(selectedPage.selected);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const courses = result.courses.filter((c) => c?.slug);
  const loading = result.loading || waitingForCategory;
  const hasFilters = Boolean(keyword || categorySlug);

  return (
    <main className="courses-page">
      <section className="ac-head">
        <div className="container">
          <span className="ac-eyebrow">
            <span className="ac-eyebrow-dot" /> {t("allCourse.eyebrow")}
          </span>
          <h1 className="ac-title">{t("allCourse.title")}</h1>
          <p className="ac-subtitle">{t("allCourse.subtitle")}</p>

          <form className="ac-search" onSubmit={handleSearch} role="search">
            <i className="fa-light fa-search ac-search-icon" aria-hidden="true" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("allCourse.search_placeholder")}
              aria-label={t("allCourse.search_placeholder")}
            />
            <button type="submit" className="ac-search-btn">{t("allCourse.search")}</button>
          </form>
        </div>
      </section>

      <section className="ac-body">
        <div className="container">
          {categoryList.length > 0 && (
            <ul className="ac-categories" aria-label={t("allCourse.categories_label")}>
              <li>
                <button type="button" className={`ac-category${!categorySlug ? ' is-active' : ''}`} aria-pressed={!categorySlug} onClick={() => updateParams({ category: null })}>
                  {t("allCourse.all")}
                </button>
              </li>
              {categoryList.map((item) => (
                <li key={item.slug}>
                  <button type="button" className={`ac-category${categorySlug === item.slug ? ' is-active' : ''}`} aria-pressed={categorySlug === item.slug} onClick={() => updateParams({ category: item.slug })}>
                    {item.name}
                  </button>
                </li>
              ))}
            </ul>
          )}

          {keyword && (
            <div className="ac-results-bar">
              <span>{t("allCourse.results_for", { keyword })}</span>
              <button type="button" className="ac-clear" onClick={() => updateParams({ keyword: null })}>
                <i className="fa-light fa-xmark" /> {t("allCourse.clear")}
              </button>
            </div>
          )}

          {loading ? (
            <div className="row g-4">
              {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
                <div className="col-xl-3 col-lg-4 col-sm-6" key={i}>
                  <FeaturedCourseSkeleton />
                </div>
              ))}
            </div>
          ) : result.error ? (
            <div className="ac-empty">
              <span className="ac-empty-icon ac-empty-icon--error"><i className="fa-light fa-wifi-exclamation" /></span>
              <h2>{t("allCourse.error_title")}</h2>
              <p>{t("allCourse.error_desc")}</p>
              <button type="button" className="ac-btn" onClick={() => setReloadKey((k) => k + 1)}>{t("allCourse.retry")}</button>
            </div>
          ) : courses.length === 0 ? (
            <div className="ac-empty">
              <span className="ac-empty-icon"><i className="fa-light fa-magnifying-glass" /></span>
              <h2>{t("allCourse.empty_title")}</h2>
              <p>{t("allCourse.empty_desc")}</p>
              {hasFilters && (
                <button type="button" className="ac-btn" onClick={() => setSearchParams({})}>{t("allCourse.reset_filters")}</button>
              )}
            </div>
          ) : (
            <div className="row g-4">
              {courses.map((course) => (
                <div className="col-xl-3 col-lg-4 col-sm-6" key={course.id}>
                  <FeaturedCourseCard course={course} />
                </div>
              ))}
            </div>
          )}

          {!loading && !result.error && result.total_page > 1 && (
            <div className="ac-pagination">
              <ReactPaginate
                pageCount={result.total_page}
                onPageChange={handlePageChange}
                forcePage={currentPage}
                previousLabel={<i className="fa-light fa-arrow-left" />}
                nextLabel={<i className="fa-light fa-arrow-right" />}
                breakLabel="…"
                containerClassName={'pagination'}
                previousLinkClassName={'page-link'}
                breakClassName={'page-link'}
                nextLinkClassName={'page-link'}
                pageClassName={'page-item'}
                pageLinkClassName="page-link"
                disabledClassName={'disabled'}
                activeClassName={'active'}
              />
            </div>
          )}
        </div>
      </section>
    </main>
  )
}
